/**
 * 服務開關（build-time）：部署環境未部署某些後端服務時，於前端明確停用並 fail-closed。
 *
 * REACT_APP_DISABLED_SERVICES：逗號分隔，可用值 order、payment、market、im、admin。
 * 未設定時全部啟用（既有建置行為不變）。
 *
 * 停用後：
 * - API client 於送出前直接拒絕（不發出網路請求），錯誤碼 SERVICE_DISABLED
 * - Order Socket.IO／IM WebSocket URL 為空，訂閱與連線皆為 no-op（不會無限重連）
 */

export const KNOWN_SERVICES = Object.freeze(['order', 'payment', 'market', 'im', 'admin']);

export const SERVICE_DISABLED_CODE = 'SERVICE_DISABLED';
export const SERVICE_DISABLED_MESSAGE = '此功能於目前環境尚未開放';

/**
 * @param {string|undefined|null} raw
 * @returns {Set<string>} 已停用的服務（未知名稱忽略）
 */
export function parseDisabledServices(raw) {
  if (!raw) {
    return new Set();
  }
  return new Set(
    String(raw)
      .split(',')
      .map((s) => s.trim().toLowerCase())
      .filter((s) => KNOWN_SERVICES.includes(s))
  );
}

export const DISABLED_SERVICES = parseDisabledServices(process.env.REACT_APP_DISABLED_SERVICES);

/**
 * @param {string} service
 * @param {Set<string>} [disabled]
 */
export function isServiceEnabled(service, disabled = DISABLED_SERVICES) {
  return !disabled.has(service);
}

// 經 member BFF 轉呼叫 payment 的路徑（前綴 /formosa-member/member/…）
const MEMBER_BFF_PAYMENT_RE = /^\/formosa-member\/member\/(coinPayType|deposit|withdraw|memberTransaction)(\/|$)/;

const SERVICE_PREFIXES = [
  ['order', /^\/formosa-order(\/|$)/],
  ['payment', /^\/formosa-payment(\/|$)/],
  ['market', /^\/formosa-market(\/|$)/],
  ['admin', /^\/formosa-admin(\/|$)/],
  ['im', /^\/im(\/|$)/],
  ['payment', MEMBER_BFF_PAYMENT_RE],
];

function toPathname(url) {
  if (!url) {
    return '';
  }
  try {
    return new URL(String(url), 'http://local.invalid').pathname;
  } catch {
    return String(url).split('?')[0];
  }
}

/**
 * @param {string} url 相對或絕對 URL
 * @returns {string|null} 對應服務名稱；不屬於可停用服務時為 null
 */
export function resolveServiceForUrl(url) {
  const path = toPathname(url);
  const hit = SERVICE_PREFIXES.find(([, re]) => re.test(path));
  return hit ? hit[0] : null;
}

/**
 * @param {string} url
 * @param {Set<string>} [disabled]
 * @returns {string|null} 若 URL 屬於已停用服務，回傳服務名稱；否則 null
 */
export function disabledServiceForUrl(url, disabled = DISABLED_SERVICES) {
  const service = resolveServiceForUrl(url);
  return service && !isServiceEnabled(service, disabled) ? service : null;
}
